import React from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { ReactComponent as IconQuestion } from "@assets/Icons/Icon_Question.svg";
import {
  NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS,
  NEXTHOP_WATCH_MAX_INTERVAL_SECONDS,
  NEXTHOP_WATCH_MIN_INTERVAL_SECONDS,
  type NexthopVerificationFormValues,
} from "../../../../../helpers/cloudPropagationHelpers";

/** The one line an operator needs: what it does, on which next-hops. */
const VERIFICATION_SUMMARY =
  "Re-checks this rule's Dynamic ENI Resolver next-hops in AWS and re-injects when the ENI changes.";

/**
 * Detail behind the "?": the bits the summary leaves out. The cadence is per
 * CPR (CTX-347) — each rule runs on its own timer and the reflector groups
 * rules that share an interval into one pass — so the tooltip carries that scope.
 */
const VERIFICATION_TOOLTIP = [
  "Runs on a timer, so it catches an ENI that changes or comes back with no BGP re-announce.",
  `Interval ${NEXTHOP_WATCH_MIN_INTERVAL_SECONDS}–${NEXTHOP_WATCH_MAX_INTERVAL_SECONDS}s (default ${NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS}s).`,
  "Rules sharing an interval share one pass, and a next-hop used by several rules is checked at the fastest of their intervals.",
].join(" ");

const OFF_WARNING =
  "Without verification, an ENI change with no BGP re-announce leaves this " +
  "rule's routes blackholed until the next full rescan.";

interface NexthopVerificationSettingsProps {
  values: NexthopVerificationFormValues;
  /** Receives only the changed fields; the caller merges them into its form state. */
  onChange: (patch: NexthopVerificationFormValues) => void;
}

/** Whole seconds only; empty/non-numeric stays undefined so validation catches it on save. */
const parseIntervalNumber = (raw: string): number | undefined => {
  const leadingDigits = raw.match(/^\s*(\d+)/)?.[1];
  return leadingDigits === undefined ? undefined : Number.parseInt(leadingDigits, 10);
};

/**
 * "Next-hop verification": this CPR's watch cadence and per-rule kill switch
 * (CTX-347). Replaces the Retry block on reflectors ≥ NEXTHOP_WATCH_MIN_VERSION.
 * Rendered ONCE per rule — below the VRF list in Telco (one value for the
 * whole rule, every VRF row carries it), in the next-hop card in Enterprise —
 * and only when the rule actually uses the Dynamic ENI Resolver
 * (`hasDynamicEniResolverNextHop`), which is the sole caller-side gate.
 */
const NexthopVerificationSettings: React.FC<NexthopVerificationSettingsProps> = ({
  values,
  onChange,
}) => {
  // Untouched block: nothing is sent on save and the stored value stays in
  // force; the default is shown so the operator sees the effective cadence.
  const configured = values.nexthopVerificationEnabled !== undefined;
  const enabled = values.nexthopVerificationEnabled ?? true;
  const interval = configured
    ? values.nexthopVerificationIntervalSeconds
    : NEXTHOP_WATCH_DEFAULT_INTERVAL_SECONDS;

  const applyEdit = (patch: NexthopVerificationFormValues) =>
    onChange({
      nexthopVerificationEnabled: enabled,
      nexthopVerificationIntervalSeconds: interval,
      ...patch,
    });

  const intervalOutOfRange =
    interval !== undefined &&
    (interval < NEXTHOP_WATCH_MIN_INTERVAL_SECONDS ||
      interval > NEXTHOP_WATCH_MAX_INTERVAL_SECONDS);

  return (
    <div>
      <div className="text-semantic-text-primary mb-2 text-body-large">
        Next-hop verification
      </div>
      <div className="text-semantic-text-secondary text-body-small mb-2 flex items-center gap-1.5">
        <span>{VERIFICATION_SUMMARY}</span>
        {/* The detail hangs off the summary, so the control row stays a plain toggle + interval. */}
        <GenericTooltip
          text={VERIFICATION_TOOLTIP}
          position="top"
          width="24rem"
          icon={<IconQuestion className="w-4 h-4 text-current" />}
          triggerWidthClass="shrink-0"
        />
      </div>
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex items-center gap-2 h-10">
          <span className="text-semantic-text-primary text-body">Verification</span>
          <GenericSwitch
            checked={enabled}
            onChange={(v) => applyEdit({ nexthopVerificationEnabled: v })}
          />
          <span className="text-semantic-text-secondary text-body-small w-6">
            {enabled ? "On" : "Off"}
          </span>
        </div>
        <div className="w-32">
          <GenericInput
            label="Interval"
            type="number"
            unit="sec"
            value={interval ?? ""}
            onChange={(v) =>
              applyEdit({ nexthopVerificationIntervalSeconds: parseIntervalNumber(v) })
            }
            hasError={intervalOutOfRange}
            disabled={!enabled}
            className="text-body"
          />
        </div>
      </div>
      {!enabled && (
        <p className="text-xs text-semantic-text-warning mt-2">{OFF_WARNING}</p>
      )}
    </div>
  );
};

export default NexthopVerificationSettings;
